/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch } from 'react-redux';
import { colors, mixins } from '@blend/consumer-components';

import { useParams } from 'react-router';
import { pageSize } from '../../components/common/Pipeline/constants';
import { JournalPipeline } from '../../components/Journal/Pipeline/JournalPipeline';
import { JournalEntryModal } from '../../components/Journal/JournalEntryModal/JournalEntryModal';
import { fetchJournalEntry } from '../../actions/journalActions';
import { notaryAPI } from '../../api/notary';
import { JournalEntry } from '../../components/Journal/Pipeline/configs';
import { NavigationSidebar } from '../../components/common/NavigationSidebar/NavigationSidebar';
import { PARAMS } from '../../definitions/RouterStates';

const sidebarWidth = 70;
const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    backgroundColor: colors.snowPlus1,
    overflow: 'hidden',
    position: 'relative',
  },
  journalWrapper: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
  sidebar: {
    width: sidebarWidth,
    height: '100%',
    backgroundColor: colors.night90,
    ...mixins.paddingVertical(28),
  },
  pipeline: {
    width: '100%',
    height: '100%',
  },
  clickable: {
    display: 'flex',
    justifyContent: 'center',
  },
  buttonStyle: {
    position: 'relative',
    height: 28,
    backgroundColor: 'transparent',
    color: colors.white,
    border: 'none',
  },
});

export namespace JournalPage {
  export interface Props {}
}

export const JournalPage: React.FC<JournalPage.Props> = () => {
  const dispatch = useDispatch();
  const { baseJournalEntryId } = useParams<{ [PARAMS.BASE_JOURNAL_ENTRY_ID]: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  const onOpenEntry = (entry: JournalEntry) => {
    openEntry(entry.id, entry.status === 'Locked');
  };

  const openEntry = (entryId: string, isLocked: boolean) => {
    setIsOpen(true);
    fetchJournalEntry(entryId, isLocked)(dispatch);
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const journalEntries = await notaryAPI.journal.getJournalEntries(pageSize, page * pageSize);
      setEntries(journalEntries.results);
      setTotalCount(journalEntries.totalCount);
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      fetchEntries();
    }
  }, [page, isOpen]);

  useEffect(() => {
    if (baseJournalEntryId) {
      openEntry(baseJournalEntryId, false);
    }
  }, [baseJournalEntryId]);

  return (
    <div className={css(styles.wrapper)} data-it="JournalPage">
      <NavigationSidebar />
      <div className={css(styles.journalWrapper)}>
        <div className={css(styles.pipeline)}>
          <JournalPipeline
            openEntry={onOpenEntry}
            entries={entries}
            isLoading={isLoading}
            hasError={hasError}
            page={page}
            totalCount={totalCount}
            setPage={setPage}
          />
          <JournalEntryModal isOpen={isOpen} handleClose={() => setIsOpen(false)} />
        </div>
      </div>
    </div>
  );
};
